import { HttpParams } from "@angular/common/http";
import { Component, EventEmitter, forwardRef, Inject, Input, Output } from "@angular/core";
import { Value } from "../../model";
import { DataService } from "../../service/data.service";
import { AbstractThingContextService } from "../../shared/class/abstract-thing-context-service.class";
import { ChartData } from "./daily-summary-grid-widget.component";

@Component({
    selector: 'daily-summary-grid-widget-metric-details',
    template: require('./daily-summary-grid-widget-metric-details.component.html'),
    styles: [require('./daily-summary-grid-widget-metric-details.component.css')]
})
export class DailySummaryGridWidgetMetricDetailsComponent {

    @Input() set data(data: ChartData) {
        this._data = data;
        this.computeTableData();
    }

    @Input() timezone: string;

    @Input() locale: string;

    @Output() closeDetails = new EventEmitter<void>();

    title: string;
    tableData: TableDataElement[];
    _data: ChartData;
    metrics: { name: string, label: string, unit: string, filter: any, filterArg: any }[];
    visibleColumns: string[];
    dataLoaded: boolean;

    constructor(
        @Inject(forwardRef(() => DataService)) private dataService: DataService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService
    ) { }

    private computeTableData(): void {
        this.metrics = [];
        this.dataLoaded = false;
        if (this._data?.value.metricDataHighlights?.length) {
            let tableData: TableDataElement[] = [];
            this._data.value.metricDataHighlights.forEach(data => {
                if (!this.metrics.some(m => m.name == data.metricName)) {
                    this.metrics.push({ name: data.metricName, label: data.metricLabel, unit: data.unit, filter: data.filter, filterArg: data.filterArg });
                }
                let tableDataElement = tableData.find(d => d.timestamp == data.timestamp);
                if (!tableDataElement) {
                    let dataMap: { [metric: string]: { value: any, filter: any, filterArg: any } } = {};
                    dataMap[data.metricLabel] = { value: data.value, filter: data.filter, filterArg: data.filterArg };
                    tableData.push({ timestamp: data.timestamp, data: dataMap, previousValue: false });
                } else {
                    tableDataElement.data[data.metricLabel] = { value: data.value, filter: data.filter, filterArg: data.filterArg };
                }
            });
            tableData.sort((a, b) => a.timestamp - b.timestamp);
            this.title = this._data.category;
            this.visibleColumns = ['timestamp'].concat(this.metrics.map(m => { return m.label }).sort());
            this.getPreviousValues(tableData[0].timestamp).then(previousValues => {
                this.tableData = (previousValues.concat(tableData));
                this.dataLoaded = true;
            });
        } else {
            this.dataLoaded = true;
            this.title = null;
            this.tableData = [];
        }
    }

    private getPreviousValues(timestamp: number): Promise<TableDataElement[]> {
        let promises = [];
        this.metrics.forEach(m => {
            promises.push(this.dataService.getLastValueByThingIdAndMetricName(this.thingContextService.getCurrentThing().id, m.name, new HttpParams().set('endDate', timestamp - 1)).then(value => {
                return { metricLabel: m.label, value: value, filter: m.filter, filterArg: m.filterArg };
            }));
        });
        return Promise.all(promises).then((previousValueResults: { metricLabel: string, value: Value, filter: any, filterArg: any }[]) => {
            let results: TableDataElement[] = [];
            previousValueResults.forEach(previousValue => {
                if (previousValue.value != null) {
                    let tableDataElement = results.find(d => d.timestamp == previousValue.value.timestamp);
                    if (!tableDataElement) {
                        let dataMap: { [metric: string]: { value: any, filter: any, filterArg: any } } = {};
                        dataMap[previousValue.metricLabel] = { value: previousValue.value.value, filter: previousValue.filter, filterArg: previousValue.filterArg };
                        results.push({ timestamp: previousValue.value.timestamp, data: dataMap, previousValue: true });
                    } else {
                        tableDataElement.data[previousValue.metricLabel] = { value: previousValue.value.value, filter: previousValue.filter, filterArg: previousValue.filterArg };
                    }
                }
            })
            return results.sort((a, b) => a.timestamp - b.timestamp);
        }).catch(() => { return [] });
    }
}

class TableDataElement {
    timestamp: number;
    data: { [metricLabel: string]: { value: any, filter: any, filterArg: any } };
    previousValue: boolean;
}